import { User, UserPatch, Controllers } from "@infoland/iprova-api";
import { ActionTree } from "vuex";
import { RootState } from "@/store";
import i18n from "@/plugins/i18n";

// state type
export interface UserState {
    loggedOnUser: User;
}

export function initialize(): UserState {
    return {
        loggedOnUser: null
    };
}

// initial state
const state: UserState = initialize();

// getters for this store
const getters = {
    /**
     * Gets the logged on user
     * @param state The state to read from
     */
    loggedOnUser(state: UserState): User {
        return state.loggedOnUser;
    }
};

// mutations for this store
const mutations = {
    /**
     * Changes the logged on user (private, do not use outside this file!)
     * @param state The state to write to
     * @param user  The new user data
     */
    _loggedOnUser(state: UserState, user: User): void {
        // undefined should become null
        if (!user) user = null;

        state.loggedOnUser = user;

        i18n.locale = user.language.substr(0, 2);
    },
    _unreadTasksAmount(state: UserState, amount: number): void {
        state.loggedOnUser.statistics.unread_tasks_amount = amount;
    },
    _unreadNotificationsAmount(state: UserState, amount: number): void {
        state.loggedOnUser.statistics.unread_notifications_amount = amount;
    }
};

// actions for this store
const actions: ActionTree<UserState, RootState> = {
    /**
     * Load current user data
     */
    async loadLoggedOnUser({ commit }): Promise<User> {
        // load user data for current user
        const user: User = await Controllers.GenericController.getAsync<User>("users/me?include_statistics=true");
        commit("_loggedOnUser", user);

        return user;
    },

    /**
     * Changes the current user
     * @param context The store context
     * @param user changes
     */
    async saveUserAsync({ dispatch }, user: { userId: string; userProps: Partial<UserPatch> }): Promise<boolean> {
        const patchResult = await Controllers.UsersController.PatchUserAsync(user.userId, user.userProps as UserPatch);

        if (patchResult) await dispatch("loadLoggedOnUser");

        return patchResult;
    },

    // Update unread tasks amount
    updateUnreadTasksAmount({ commit }, { amount }): void {
        commit("_unreadTasksAmount", amount);
    },

    // Update unread notifications amount
    updateUnreadNotificationsAmount({ commit }, { amount }): void {
        commit("_unreadNotificationsAmount", amount);
    }
};

// export the namespace
export const namespace = "user";

// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
